import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/dateUtils';
import { intakeTokenFor } from '../utils/hotelUtils';
import { useLazyBookingSignatures, withSignatures } from '../hooks/useLazyBookingSignatures';

const STATUS_LABELS = { booked: 'Reservado', 'checked-in': 'Ingresado', 'checked-out': 'Salida', cancelled: 'Cancelado' };

const nightsBetween = (checkIn, checkOut) => {
    const ms = new Date(checkOut).getTime() - new Date(checkIn).getTime();
    return Number.isFinite(ms) ? Math.max(0, Math.ceil(ms / (1000 * 3600 * 24))) : 0;
};

const sameDay = (a, b) => new Date(a).toISOString().slice(0, 10) === new Date(b).toISOString().slice(0, 10);

// The vet details recorded for one dog at check-in or check-out, one line.
const VetSummary = ({ label, vet }) => {
    if (!vet || !(vet.weight || vet.temperature || vet.notes || vet.medication || vet.checkedBy)) return null;
    const vitals = [vet.weight && `${vet.weight} kg`, vet.temperature && `${vet.temperature}°C`].filter(Boolean).join(' · ');
    return (
        <div className="rc-dog-vet">
            <strong>{label}:</strong> {vitals}
            {vet.checkedBy && <span className="rc-dog-vet-by"> — {vet.checkedBy}</span>}
            {vet.notes && <div>Notas: {vet.notes}</div>}
            {vet.medication && <div>Medicación: {vet.medication}</div>}
        </div>
    );
};

/**
 * One card for every dog of a multi-dog reservation: shared dates, owner and
 * intake link up top, a row per dog, and one check-in / check-out for the lot.
 */
const HotelReservationCard = ({ bookings, onEdit, onDelete, onCheckIn, onCheckOut, onEditVetCheckin }) => {
    const navigate = useNavigate();
    const cardRef = useRef(null);
    const signatures = useLazyBookingSignatures(bookings, cardRef);

    const first = bookings[0];
    const dogNames = bookings.map(b => b.dog?.name || 'Perro desconocido');
    const ownerNames = first.dog?.owners?.map(o => o.name).join(', ') || 'Propietario desconocido';
    const nights = nightsBetween(first.checkIn, first.checkOut);
    const statuses = [...new Set(bookings.map(b => b.status))];
    const toCheckIn = bookings.filter(b => b.status === 'booked');
    const toCheckOut = bookings.filter(b => b.status === 'checked-in');
    const intakeDone = bookings.filter(b => b.intakeForm?.submittedAt).length;
    const notes = first.notes;

    // A grouped check-in stores the same signature on every dog; show each
    // distinct one once rather than repeating the image per dog.
    const distinct = (key) => [...new Set(bookings.map(b => signatures[b._id]?.[key]).filter(Boolean))];
    const checkInSignatures = distinct('checkIn');
    const checkOutSignatures = distinct('checkOut');

    const intakeToken = intakeTokenFor(first);

    const handleShareForm = () => {
        const url = `${window.location.origin}/hotel/intake/${intakeToken}`;
        const ownerPhone = first.dog?.owners?.[0]?.phone;
        const text = `Hola! Te compartimos el formulario de ingreso de *${dogNames.join(', ')}* al hotel. Por favor completalo antes de su llegada:\n${url}`;
        if (ownerPhone) {
            window.open(`https://wa.me/${ownerPhone.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`, '_blank');
        } else if (navigator.share) {
            navigator.share({ title: 'Formulario de ingreso', url, text }).catch(() => {});
        } else {
            navigator.clipboard?.writeText(url);
            alert('Enlace copiado al portapapeles');
        }
    };

    return (
        <div className="hotel-booking-card rc-card" ref={cardRef}>
            <div className="booking-header">
                <h3>🏨 {dogNames.join(', ')}</h3>
                {statuses.length === 1 && (
                    <span className={`status-badge ${statuses[0]}`}>{STATUS_LABELS[statuses[0]] || statuses[0]}</span>
                )}
            </div>

            <div className="booking-info">
                <p className="rc-count">Reserva de {bookings.length} perros</p>
                <p className="owner-names">Propietario(s): {ownerNames}</p>
                <p className="checkin-date">Entrada: {formatDate(first.checkIn)}</p>
                <p className="checkout-date">Salida: {formatDate(first.checkOut)}</p>
                <p className="duration">Duración: {nights} noche{nights !== 1 ? 's' : ''}</p>
            </div>

            {notes && (
                <div className="booking-notes">
                    <strong>Notas:</strong> {notes}
                </div>
            )}

            <div className="rc-dogs">
                {bookings.map(booking => {
                    const datesDiffer = !sameDay(booking.checkIn, first.checkIn) || !sameDay(booking.checkOut, first.checkOut);
                    return (
                        <div key={booking._id} className="rc-dog">
                            <div className="rc-dog-head">
                                <span className="rc-dog-name">🐕 {booking.dog?.name || 'Perro desconocido'}</span>
                                <span className="rc-dog-breed">{booking.dog?.breed}</span>
                                {statuses.length > 1 && (
                                    <span className={`status-badge ${booking.status}`}>{STATUS_LABELS[booking.status] || booking.status}</span>
                                )}
                            </div>
                            {datesDiffer && (
                                <div className="rc-dog-dates">{formatDate(booking.checkIn)} → {formatDate(booking.checkOut)}</div>
                            )}
                            {booking.notes && booking.notes !== notes && (
                                <div className="rc-dog-vet"><strong>Notas:</strong> {booking.notes}</div>
                            )}
                            <VetSummary label="🩺 Entrada" vet={booking.vetCheckIn} />
                            <VetSummary label="🩺 Salida" vet={booking.vetCheckOut} />
                            <div className="rc-dog-actions">
                                {(booking.status === 'booked' || booking.status === 'checked-in') && onEdit && (
                                    <button onClick={() => onEdit(booking)} className="edit-btn" title={`Editar reserva de ${booking.dog?.name}`}>
                                        Editar
                                    </button>
                                )}
                                {booking.status === 'checked-in' && onEditVetCheckin && (
                                    <button
                                        onClick={() => onEditVetCheckin(withSignatures(booking, signatures[booking._id]))}
                                        className="vet-edit-btn"
                                        title="Editar información de salud de entrada"
                                    >
                                        🩺 Salud entrada
                                    </button>
                                )}
                                {booking.status === 'booked' && onDelete && (
                                    <button onClick={() => onDelete(booking._id)} className="delete-btn" title={`Eliminar reserva de ${booking.dog?.name}`}>
                                        Eliminar
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {(checkInSignatures.length > 0 || checkOutSignatures.length > 0) && (
                <div className="vet-checkin-info">
                    {checkInSignatures.map(sig => (
                        <div key={`in-${sig.length}-${sig.slice(-24)}`} className="signature-display">
                            <strong>Firma del propietario (entrada):</strong>
                            <img src={sig} alt="Firma del propietario" className="signature-display-img" />
                        </div>
                    ))}
                    {checkOutSignatures.map(sig => (
                        <div key={`out-${sig.length}-${sig.slice(-24)}`} className="signature-display">
                            <strong>Firma del propietario (salida):</strong>
                            <img src={sig} alt="Firma del propietario" className="signature-display-img" />
                        </div>
                    ))}
                </div>
            )}

            <div className="card-actions">
                <button
                    onClick={() => navigate(`/hotel/intake/${intakeToken}/staff`)}
                    className="intake-form-btn"
                    title="Formulario de ingreso de todos los perros"
                >
                    {intakeDone > 0 ? `📋 Ver formulario (${intakeDone}/${bookings.length})` : '📋 Formulario de ingreso'}
                </button>
                {intakeDone > 0 && (
                    <button
                        onClick={() => navigate(`/hotel/intake/${intakeToken}/summary`)}
                        className="intake-form-btn"
                        title="Ver resumen para el equipo"
                    >
                        🗒️ Ver resumen
                    </button>
                )}
                <button onClick={handleShareForm} className="share-form-btn" title="Compartir formulario al dueño">
                    📤 Compartir
                </button>
                {toCheckIn.length > 0 && onCheckIn && (
                    <button onClick={() => onCheckIn(toCheckIn)} className="checkin-btn" title="Registrar entrada de la reserva">
                        Entrada ({toCheckIn.length})
                    </button>
                )}
                {toCheckOut.length > 0 && onCheckOut && (
                    <button onClick={() => onCheckOut(toCheckOut)} className="checkout-btn" title="Registrar salida de la reserva">
                        Salida ({toCheckOut.length})
                    </button>
                )}
            </div>
        </div>
    );
};

export default HotelReservationCard;
